import { createReducer, on } from '@ngrx/store';
import {
  initialUniversalFilterState,
  UniversalFilterState,
} from './universal-filter.state';
import * as UniversalFilterActions from './universal-filter.actions';
import {
  FilterDateOptions,
  Option,
} from 'src/app/shared/component/filter/universal-filter/universal-filter.interface';
import { resolveFilterDateRange } from 'src/app/shared/component/filter/universal-filter/universal-filter-helper.service';
import { loginSuccess } from 'src/app/store/user/user.actions';

const getDefaultDateOption = (options: Option[] = []): Option | null => {
  if (!options.length) {
    return null;
  }

  return options.find((option) => option.default) ?? options[0];
};

const resolveDateFilter = (
  currentDate: UniversalFilterState['filters']['date'],
  dateOptions: FilterDateOptions | null
) => {
  if (!dateOptions?.options?.length) {
    return currentDate;
  }

  const matchingOption = currentDate?.id
    ? dateOptions.options.find((option) => option.id === currentDate.id)
    : null;

  // Custom date ranges (id: 'custom1') are user-picked and won't appear in
  // any page's preset option list. Preserve them as-is so navigating to a
  // different feature page doesn't silently replace the selection with the
  // page's default preset.
  // Exception: if the destination component enforces a stricter days limit
  // and the stored custom range exceeds it, fall through to the default
  // option so the UI never receives an out-of-range selection.
  if (
    !matchingOption &&
    currentDate?.id === 'custom1' &&
    currentDate?.startDate
  ) {
    const daysLimit = dateOptions?.days;
    if (!daysLimit || !currentDate.endDate) {
      return currentDate;
    }
    const diffDays = Math.ceil(
      (new Date(currentDate.endDate).getTime() -
        new Date(currentDate.startDate).getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (diffDays <= daysLimit) {
      return currentDate;
    }
    // Range exceeds the new component's days limit — fall through to default.
  }

  const nextOption =
    matchingOption ?? getDefaultDateOption(dateOptions.options);

  if (!nextOption) {
    return currentDate;
  }

  const { startDate, endDate } = resolveFilterDateRange(
    nextOption.id,
    [],
    matchingOption
      ? {
          startDate: currentDate?.startDate,
          endDate: currentDate?.endDate,
        }
      : undefined
  );

  return {
    ...nextOption,
    startDate,
    endDate,
  };
};

export const universalFilterReducer = createReducer(
  initialUniversalFilterState,
  on(UniversalFilterActions.setUniversalFilter, (state, { filters }) => ({
    ...state,
    filters: { ...filters },
  })),
  on(UniversalFilterActions.updateUniversalFilter, (state, { filters }) => ({
    ...state,
    filters: { ...state.filters, ...filters },
  })),
  on(UniversalFilterActions.clearUniversalFilter, (state) => ({
    ...state,
    filters: {},
  })),
  on(
    UniversalFilterActions.resetUniversalFilter,
    () => initialUniversalFilterState
  ),
  on(UniversalFilterActions.setFilterConfig, (state, { config }) => ({
    ...state,
    config: { ...config },
  })),
  on(UniversalFilterActions.setFilterDateOptions, (state, { dateOptions }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...(resolveDateFilter(state.filters.date, dateOptions)
        ? { date: resolveDateFilter(state.filters.date, dateOptions) }
        : {}),
    },
    dateOptions: {
      ...dateOptions,
      options: [...dateOptions.options],
    },
  })),
  on(UniversalFilterActions.clearFilterConfig, (state) => ({
    ...state,
    config: {},
  })),
  on(UniversalFilterActions.clearFilterDateOptions, (state) => ({
    ...state,
    dateOptions: null,
  })),
  // Seed a component-specific default date without touching other filters.
  on(UniversalFilterActions.setDefaultDateFilter, (state, { dateOption }) => ({
    ...state,
    filters: {
      ...state.filters,
      date: state.filters.date ?? dateOption,
    },
  })),
  on(loginSuccess, () => initialUniversalFilterState)
);
